/**
 * Phase 16 — engagement client (web): price watches, daily pick, and
 * light localStorage personal memory. Mirrors mobile/lib/engagement.ts.
 * Identity = locally generated device id sent as `X-Device-Id`.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const DEVICE_KEY = "piqpath_device_id";
const MEMORY_KEY = "piqpath_memory_v1";

export function getDeviceId() {
    try {
        let id = localStorage.getItem(DEVICE_KEY);
        if (!id) {
            id = `web_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
            localStorage.setItem(DEVICE_KEY, id);
        }
        return id;
    } catch {
        return "web_anon";
    }
}

const hdrs = () => ({ "X-Device-Id": getDeviceId() });

// ── Price watches ────────────────────────────────────────────────────
export async function createWatch(input) {
    const res = await axios.post(`${API}/watches`, input, { headers: hdrs() });
    return res.data;
}

export async function listWatches() {
    const res = await axios.get(`${API}/watches`, { headers: hdrs() });
    return res.data;
}

export async function deleteWatch(id) {
    await axios.delete(`${API}/watches/${id}`, { headers: hdrs() });
}

// ── Daily pick ───────────────────────────────────────────────────────
export async function fetchDailyPick(originIata, currency) {
    try {
        const res = await axios.get(`${API}/daily-pick`, {
            params: { origin_iata: originIata, currency },
            headers: hdrs(),
        });
        return res.data;
    } catch {
        return null;
    }
}

// ── Personal memory (localStorage) ───────────────────────────────────
const EMPTY = { recent_destinations: [] };

export function loadMemory() {
    try {
        const raw = localStorage.getItem(MEMORY_KEY);
        if (!raw) return { ...EMPTY };
        const m = JSON.parse(raw);
        return {
            home_iata: m.home_iata ?? null,
            recent_destinations: Array.isArray(m.recent_destinations)
                ? m.recent_destinations.slice(0, 6)
                : [],
            last_currency: m.last_currency ?? null,
        };
    } catch {
        return { ...EMPTY };
    }
}

function save(m) {
    try { localStorage.setItem(MEMORY_KEY, JSON.stringify(m)); } catch {}
}

export function rememberHome(iata, currency) {
    const m = loadMemory();
    if (iata) m.home_iata = String(iata).toUpperCase();
    if (currency) m.last_currency = String(currency).toUpperCase();
    save(m);
}

export function extractDestination(query) {
    const q = (query || "").trim();
    const m =
        q.match(/\b(?:to|in|for)\s+([A-Z][A-Za-z]+(?:\s[A-Z][A-Za-z]+)?)/) ||
        q.match(/^(?:plan a trip to|flights to|hotels in)\s+(.+?)(?:\s+(?:on|for|from)\b|$)/i);
    if (!m) return null;
    const dest = m[1].trim().replace(/[?.!,]$/, "");
    if (dest.length < 3 || dest.length > 30) return null;
    return dest;
}

export function rememberQuery(query) {
    const dest = extractDestination(query);
    if (!dest) return;
    const m = loadMemory();
    m.recent_destinations = [
        dest,
        ...m.recent_destinations.filter((d) => d.toLowerCase() !== dest.toLowerCase()),
    ].slice(0, 6);
    save(m);
}
